import React, { useState } from 'react'
import {
    HiChevronDown,
    HiChevronUp,
    HiOutlineCheckCircle,
    HiOutlineClock,
    HiOutlineDocumentText,
    HiOutlineExclamationCircle,
} from 'react-icons/hi'

var vs = 1

const Accordion = ({ children, className = '' }) => {
    const [expandedItems, setExpandedItems] = useState({})

    const toggleAccordion = (itemId) => {
        setExpandedItems((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }))
    }

    return (
        <div
            className={`space-y-4 ${className}`}
            data-accordion-context={{ expandedItems, toggleAccordion }}
        >
            {children}
        </div>
    )
}

const Group = ({ children, version = 1 }) => {
    vs = version
    return <div>{children}</div>
}

const Item = ({ children, id, className = '' }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const baseClassName =
        'bg-white border border-stroke rounded-lg shadow-default dark:border-strokedark dark:bg-boxdark overflow-hidden transition-all duration-300'

    let itemClassName = baseClassName
    if (vs === 2) {
        itemClassName =
            'bg-gray border border-stroke rounded-lg shadow-default dark:border-strokedark dark:bg-graydark overflow-hidden transition-all duration-300'
    } else if (vs === 3) {
        itemClassName =
            'bg-white border-2 border-primary rounded-lg shadow-lg dark:border-secondary dark:bg-boxdark overflow-hidden transition-all duration-300'
    }

    return (
        <div
            className={`${itemClassName} ${className}`}
            data-accordion-item={{ id, isExpanded, setIsExpanded }}
        >
            {children}
        </div>
    )
}

const Header = ({ children, onClick, status }) => {
    const baseClassName =
        'flex items-center justify-between p-4 cursor-pointer hover:bg-gray hover:dark:bg-graydark transition-colors duration-200 md:p-6'

    let headerClassName = baseClassName
    if (vs === 2) {
        headerClassName =
            'flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-primary hover:text-white dark:hover:bg-secondary transition-colors duration-200 md:px-6'
    } else if (vs === 3) {
        headerClassName =
            'flex items-center justify-between p-4 cursor-pointer border-b border-stroke hover:bg-primary hover:text-white dark:border-strokedark dark:hover:bg-secondary transition-colors duration-200 md:p-6'
    }

    return (
        <div
            className={`${headerClassName} border-l-8 ${status === 'red' ? 'border-red' : status === 'yellow' ? 'border-yellow' : status === 'green' ? 'border-green' : 'border-gray3'}`}
            onClick={onClick}
        >
            {children}
        </div>
    )
}

const Content = ({ children, isExpanded }) => {
    const baseClassName = 'border-t border-stroke dark:border-strokedark'

    let contentClassName = baseClassName
    if (vs === 2) {
        contentClassName =
            'border-t border-stroke dark:border-strokedark bg-gray dark:bg-graydark'
    } else if (vs === 3) {
        contentClassName = 'bg-gray dark:bg-graydark'
    }

    if (!isExpanded) return null

    return (
        <div className={contentClassName}>
            <div className="p-4 font-medium leading-relaxed text-black dark:text-white md:p-6">
                {children}
            </div>
        </div>
    )
}

const Title = ({ children, status }) => {
    const getStatusIcon = (status) => {
        switch (status) {
            case 'matched':
                return <HiOutlineCheckCircle className="w-5 h-5 text-green" />
            case 'overcharged':
                return (
                    <HiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                )
            case 'undercharged':
                return (
                    <HiOutlineExclamationCircle className="w-5 h-5 text-yellow-500" />
                )
            case 'notVerified':
                return <HiOutlineClock className="w-5 h-5 text-blue-500" />
            default:
                return (
                    <HiOutlineDocumentText className="w-5 h-5 text-black dark:text-white" />
                )
        }
    }

    return (
        <div className="flex items-center space-x-3 flex-1">
            {status && getStatusIcon(status)}
            <div className="flex-1">
                <h3 className="text-lg font-medium text-black dark:text-white md:text-xl">
                    {children}
                </h3>
            </div>
        </div>
    )
}

const Badge = ({ status, children }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'matched':
                return 'text-green bg-green/10'
            case 'overcharged':
                return 'text-red bg-red/10'
            case 'undercharged':
                return 'text-yellow bg-yellow/10'
            case 'notVerified':
                return 'text-gray3 bg-gray3/10'
            default:
                return 'text-gray bg-gray/10'
        }
    }

    const statusText = status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : ''

    const displayText =
        statusText === 'NotVerified' ? 'Not Verified' : statusText

    return (
        <span
            className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${getStatusColor(status)}`}
        >
            {displayText || children}
        </span>
    )
}

const Toggle = ({ isExpanded }) => {
    return (
        <div className="flex items-center space-x-3">
            {isExpanded ? (
                <HiChevronUp className="w-5 h-5 text-black dark:text-white" />
            ) : (
                <HiChevronDown className="w-5 h-5 text-black dark:text-white" />
            )}
        </div>
    )
}

// Example usage component
const InvoiceAccordionExample = () => {
    const accordionData = [
        {
            id: 1,
            title: 'Invoice Line Item #001',
            status: 'matched',
            amount: '$45.50',
            description: 'Transport service for Route A-B',
            details: {
                consignmentNo: 'CON-2024-001',
                pickupLocation: 'Warehouse A, Sydney',
                deliveryLocation: 'Distribution Center B, Melbourne',
                distance: '878 km',
                weight: '2.5 tons',
                rate: '$0.052/km',
                calculatedAmount: '$45.66',
                difference: '-$0.16',
                status: 'Matched - Within tolerance',
            },
        },
        {
            id: 2,
            title: 'Invoice Line Item #002',
            status: 'overcharged',
            amount: '$78.90',
            description: 'Express delivery service',
            details: {
                consignmentNo: 'CON-2024-002',
                pickupLocation: 'Port Adelaide',
                deliveryLocation: 'Brisbane Terminal',
                distance: '1,200 km',
                weight: '1.8 tons',
                rate: '$0.055/km',
                calculatedAmount: '$66.00',
                difference: '+$12.90',
                status: 'Overcharged - Rate discrepancy',
            },
        },
    ]

    const [isExpanded, setIsExpanded] = useState(false)

    return (
        <Accordion.Group version={1}>
            <Accordion>
                {accordionData.map((item) => {
                    return (
                        <Accordion.Item key={item.id} id={item.id}>
                            <Accordion.Header
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <Accordion.Title status={item.status}>
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <span>{item.title}</span>
                                            <span className="text-lg font-bold text-primary dark:text-secondary ml-4">
                                                {item.amount}
                                            </span>
                                        </div>
                                        <p className="text-sm text-black/70 dark:text-white/70 mt-1">
                                            {item.description}
                                        </p>
                                    </div>
                                </Accordion.Title>
                                <div className="flex items-center space-x-4">
                                    <Accordion.Badge status={item.status} />
                                    <Accordion.Toggle isExpanded={isExpanded} />
                                </div>
                            </Accordion.Header>

                            <Accordion.Content isExpanded={isExpanded}>
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-black/70 dark:text-white/70 uppercase tracking-wide mb-3">
                                                Consignment Details
                                            </p>
                                            <div className="space-y-2">
                                                <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Consignment No:
                                                    </span>
                                                    <span className="text-sm font-medium text-black dark:text-white">
                                                        {
                                                            item.details
                                                                .consignmentNo
                                                        }
                                                    </span>
                                                </div>
                                                <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Distance:
                                                    </span>
                                                    <span className="text-sm text-black dark:text-white">
                                                        {item.details.distance}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Weight:
                                                    </span>
                                                    <span className="text-sm text-black dark:text-white">
                                                        {item.details.weight}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-black/70 dark:text-white/70 uppercase tracking-wide mb-3">
                                                Verification Details
                                            </p>
                                            <div className="space-y-2">
                                                <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Invoiced Amount:
                                                    </span>
                                                    <span className="text-sm font-medium text-primary dark:text-secondary">
                                                        {item.amount}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between border-b border-stroke dark:border-strokedark pb-1">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Calculated Amount:
                                                    </span>
                                                    <span className="text-sm text-black dark:text-white">
                                                        {
                                                            item.details
                                                                .calculatedAmount
                                                        }
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-black dark:text-white">
                                                        Difference:
                                                    </span>
                                                    <span
                                                        className={`text-sm font-medium ${
                                                            item.status ===
                                                            'matched'
                                                                ? 'text-primary dark:text-secondary'
                                                                : item.status ===
                                                                    'overcharged'
                                                                  ? 'text-red-500'
                                                                  : 'text-yellow-500'
                                                        }`}
                                                    >
                                                        {
                                                            item.details
                                                                .difference
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 p-4 bg-gray dark:bg-graydark rounded-md">
                                    <p className="text-sm font-medium text-black dark:text-white mb-2">
                                        Status Details
                                    </p>
                                    <p
                                        className={`text-sm ${
                                            item.status === 'matched'
                                                ? 'text-primary dark:text-secondary'
                                                : item.status === 'overcharged'
                                                  ? 'text-red-500'
                                                  : 'text-yellow-500'
                                        }`}
                                    >
                                        {item.details.status}
                                    </p>
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                    )
                })}
            </Accordion>
        </Accordion.Group>
    )
}

// Compound component assignments
Accordion.Group = Group
Accordion.Item = Item
Accordion.Header = Header
Accordion.Content = Content
Accordion.Title = Title
Accordion.Badge = Badge
Accordion.Toggle = Toggle

export default Accordion
export { InvoiceAccordionExample }
